import './machine_app_jogger.css';

import $ from 'jQuery';
import widgets from '../../legacy/widgets';
import immediateApi from '../../api/immediateApi';
import { getAxisIdToPositionMapForLibrary, stopAll } from '../machineapp_utils';
import { modalMessageBox } from './machine_app_modal_toast_message_box';

/**
 * Configuration options for a jogger axis.
 *
 * @typedef {Object} JoggerAxisOption
 * @property {string} [units] - The units to use for this axis.
 */

/**
 * The letters representing each axis.
 *
 * @typedef {'x' | 'y' | 'z' | 'w'} AxisLetter
 */

/**
 * Properties for a jogger component.
 * @param {any} machineLogicLibrary
 * @param {Partial<Record<AxisLetter, string>>} pAxisMapping - A mapping of AxisLetter to string values.
 * @param {Object} [pOptions] - Additional configuration options.
 * @param {string} [pOptions.width] - Width of the jogger.
 * @param {string} [pOptions.height] - Height of the jogger.
 * @param {number} [pOptions.jogIncrement] - The increment in mm by which you will jog
 * @param {number} [pOptions.speed] - The speed in mm/s at which we will jog
 * @param {number} [pOptions.acceleration] - The acceleration in mm/s^2 at which we will jog
 * @param {JoggerAxisOption} [pOptions.x] - Configuration options for the x axis.
 * @param {JoggerAxisOption} [pOptions.y] - Configuration options for the y axis.
 * @param {JoggerAxisOption} [pOptions.z] - Configuration options for the z axis.
 * @param {JoggerAxisOption} [pOptions.w] - Configuration options for the w axis.
 * @param {(map: Record<AxisLetter, number | undefined>) => void} [pOnPositionUpdate] - Callback function that takes a mapping of AxisLetter to number values.
 * @param {string} [pInstructionText] - Instruction text to display.
 */
export const jogger = (machineLogicLibrary, pAxisMapping, pOptions, pOnPositionUpdate, pInstructionText) => {
  const lButtonsToDisableWhileJogging = [];
  let lCurrentJogIncrement = pOptions?.jogIncrement || 100;
  let lCurrentSpeed = pOptions?.speed || 100;
  let lCurrentAcceleration = pOptions?.acceleration || 100;
  let lCurrentPositionMap = undefined;

  const lGetJoggerWidget = (pAxis, pAxisOptions = {}) => {
    const lContainer = $('<div>').addClass('machineapp_jogger_axis');
    const lLabelContainer = $('<div>').addClass('machineapp_jogger_label_container');
    const lLabel = $('<label>').text(`${pAxis.toUpperCase()} Axis`);
    const lUnits = $('<span>').text(pAxisOptions?.units || '(mm)');
    const lButtonContainer = $('<div>');

    const lAxisUuid = pAxisMapping[pAxis];
    const lAxis = machineLogicLibrary.getConfigManager().getAxisByUuid(lAxisUuid);
    let lEmptyAxisWarning = undefined;

    if (lAxis === undefined) {
      lEmptyAxisWarning = `Axis ${pAxis} is not mapped to anything. Cannot jog.`;
      console.warn(lEmptyAxisWarning);
    }

    const lNegativeJogButton = $('<button>')
      .addClass('machineapp_jogger_jog_btn')
      .attr('disabled', lEmptyAxisWarning !== undefined)
      .on('click', () => lJog(-1))
      .append(
        $('<i>').addClass('far fa-minus').css({ fontSize: '22px' })
      );

    const lHomeButton = $('<button>')
      .addClass('machineapp_jogger_home_btn')
      .attr('disabled', lEmptyAxisWarning !== undefined)
      .text('Home Axis')
      .on('click', () => {
        if (lEmptyAxisWarning) {
          return;
        }

        lButtonsToDisableWhileJogging.forEach((pButton) => pButton.attr('disabled', true));
        immediateApi
          .home(lAxisUuid)
          .then(() => getAxisIdToPositionMapForLibrary(machineLogicLibrary))
          .then((pPositionsMap) => {
            lButtonsToDisableWhileJogging.forEach((pButton) => pButton.attr('disabled', false));
            lCurrentPositionMap = pPositionsMap;
          });
      });

    const lPositiveJogButton = $('<button>')
      .addClass('machineapp_jogger_jog_btn')
      .attr('disabled', lEmptyAxisWarning !== undefined)
      .on('click', () => lJog(1))
      .append(
        $('<i>').addClass('far fa-plus').css({ fontSize: '22px' })
      );

    const lJog = (pDirection) => {
      if (lEmptyAxisWarning) {
        return;
      }

      const lJogIncrement = lCurrentJogIncrement * pDirection;
      lButtonsToDisableWhileJogging.forEach((pButton) => pButton.attr('disabled', true));

      immediateApi
        .setSpeed(lAxisUuid, lCurrentSpeed)
        .then(() => immediateApi.setAcceleration(lAxisUuid, lCurrentAcceleration))
        .then(() => immediateApi.moveRelative(lAxisUuid, lJogIncrement))
        .then(() => getAxisIdToPositionMapForLibrary(machineLogicLibrary))
        .then((pPositionsMap) => {
          lButtonsToDisableWhileJogging.forEach((pButton) => pButton.attr('disabled', false));
          lCurrentPositionMap = pPositionsMap;
        });
    };

    if (lEmptyAxisWarning) {
      lContainer.attr('title', lEmptyAxisWarning);
    }

    lButtonsToDisableWhileJogging.push(lNegativeJogButton);
    lButtonsToDisableWhileJogging.push(lHomeButton);
    lButtonsToDisableWhileJogging.push(lPositiveJogButton);

    return lContainer.append(
      lLabelContainer.append(
        lLabel,
        lUnits,
      ),
      lButtonContainer.append(
        lNegativeJogButton,
        lHomeButton,
        lPositiveJogButton
      )
    );
  };

  const lJoggerContainer = $('<div>').addClass('machineapp_jogger_container');
  if (pOptions?.width) lJoggerContainer.css('width', pOptions.width);
  if (pOptions?.height) lJoggerContainer.css('height', pOptions.height);

  const lInputsContainer = $('<div>')
    .addClass('machineapp_jogger_container_inputs');

  const lJogIncrementInput = widgets.numberWidget(
    'Jog Increment',
    'The increment in mm by which you will jog',
    lCurrentJogIncrement,
    'mm',
    { min: 0 },
    (pValue) => { lCurrentJogIncrement = Number(pValue); },
    false
  );

  const lMaxSpeedInput = widgets.numberWidget(
    'Max Speed',
    'The speed in mm/s at which we will jog',
    lCurrentSpeed,
    'mm/s',
    { min: 0 },
    (pValue) => { lCurrentSpeed = Number(pValue); },
    false
  );

  const lMaxAcceleration = widgets.numberWidget(
    'Max Acceleration',
    'The acceleration in mm/s^2 at which we will jog',
    lCurrentAcceleration,
    'mm/s^2',
    { min: 0 },
    (pValue) => { lCurrentAcceleration = Number(pValue); },
    false
  );

  const lWidgetContainer = $('<div>')
    .addClass('machineapp_jogger_container_widget');

  const lInstructionText = $('<div>')
    .text(pInstructionText)
    .addClass('machineapp_jogger_container_widget_title')
    .css('display', pInstructionText ? 'inherit' : 'none');


  const lToolbarContainer = $('<div>')
    .addClass('machineapp_jogger_toolbar');

  const lSetPositionButton = $('<button>')
    .addClass('machineapp_set_pos_btn')
    .attr('disabled', true)
    .text('Set Position')
    .on('click', () => {
      pOnPositionUpdate?.(lCurrentPositionMap);
      modalMessageBox('Position has been set', 'success');
    });

  const lStopMovementButton = $('<button>')
    .addClass('machineapp_jogger_stop_btn')
    .text('Stop')
    .on('click', () => {
      lStopMovementButton.attr('disabled', true);
      stopAll(machineLogicLibrary).then(() => lStopMovementButton.attr('disabled', false));
    });

  if (pOnPositionUpdate) {
    getAxisIdToPositionMapForLibrary(machineLogicLibrary)
      .then((pPositionsMap) => {
        lCurrentPositionMap = pPositionsMap;
        lSetPositionButton.removeAttr('disabled');
      });
  }

  return lJoggerContainer.append(
    lInputsContainer.append(
      lJogIncrementInput,
      lMaxSpeedInput,
      lMaxAcceleration
    ),
    lWidgetContainer.append(
      lInstructionText,
      ('x' in pAxisMapping) && lGetJoggerWidget('x', pOptions?.x),
      ('y' in pAxisMapping) && lGetJoggerWidget('y', pOptions?.y),
      ('z' in pAxisMapping) && lGetJoggerWidget('z', pOptions?.z),
      ('w' in pAxisMapping) && lGetJoggerWidget('w', pOptions?.w),
      lToolbarContainer.append(
        pOnPositionUpdate && lSetPositionButton,
        lStopMovementButton
      )
    )
  );
};
